import React, { useState } from "react";
import { Card, Button, Badge } from "react-bootstrap";
import { User } from "../../../features/users/usersSlice";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface UserCardProps {
  user: User;
  onEdit: () => void;
  onDelete: () => void;
  getStatusBadge: (status: string) => React.ReactElement;
  getRoleBadges: (roles: string[]) => React.ReactElement[];
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onDelete,
  getStatusBadge,
  getRoleBadges,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [showActions, setShowActions] = useState(false);

  // Minimalist initials
  const getInitials = (name: string): string => {
    const words = name.trim().split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return words[0]?.substring(0, 2).toUpperCase() || "U";
  };

  // Enhanced color palette for both themes
  const getAvatarColor = (name: string): string => {
    const lightColors = [
      "#3b82f6",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#06b6d4",
      "#ec4899",
      "#84cc16",
    ];
    const darkColors = [
      "#60a5fa",
      "#34d399",
      "#fbbf24",
      "#f87171",
      "#a78bfa",
      "#22d3ee",
      "#f472b6",
      "#a3e635",
    ];
    const colors = theme == "dark" ? darkColors : lightColors;

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Theme-aware role tag colors
  const getRoleTagColor = (role: string): string => {
    const lightRoleColors: Record<string, string> = {
      admin: "#dc3545",
      manager: "#fd7e14",
      user: "#6c757d",
      editor: "#20c997",
      viewer: "#6f42c1",
      moderator: "#0d6efd",
      supervisor: "#e83e8c",
      analyst: "#17a2b8",
    };

    const darkRoleColors: Record<string, string> = {
      admin: "#f87171",
      manager: "#fb923c",
      user: "#9ca3af",
      editor: "#34d399",
      viewer: "#a78bfa",
      moderator: "#60a5fa",
      supervisor: "#f472b6",
      analyst: "#22d3ee",
    };

    const roleColors = theme == "dark" ? darkRoleColors : lightRoleColors;
    return (
      roleColors[role.toLowerCase()] ||
      (theme == "dark" ? "#9ca3af" : "#6c757d")
    );
  };

  const initials = getInitials(user.full_name);
  const avatarColor = getAvatarColor(user.full_name);
  const isOnline = user.status === "active";

  return (
    <>
      <Card
        className="border-0 h-100 user-card"
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Actions - only visible on hover */}
        <div
          className="card-actions"
          style={{
            opacity: showActions ? 1 : 0,
            transform: showActions ? "translateY(0)" : "translateY(-5px)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: 10,
          }}
        >
          <div className="d-flex gap-1">
            <Button
              size="sm"
              variant="light"
              className="action-btn edit-btn"
              onClick={onEdit}
              title={t("edit_user")}
            >
              <i className="bi bi-pencil"></i>
            </Button>
            <Button
              size="sm"
              variant="light"
              className="action-btn delete-btn"
              onClick={onDelete}
              title={t("delete_user")}
            >
              <i className="bi bi-trash"></i>
            </Button>
          </div>
        </div>

        <Card.Body className="card-body-custom">
          {/* Avatar with online indicator */}
          <div className="avatar-container">
            <div
              className="user-avatar"
              style={{ backgroundColor: avatarColor }}
            >
              {initials}
            </div>
            {isOnline && <div className="online-indicator" />}
          </div>

          {/* User info */}
          <h6 className="user-name">{user.full_name}</h6>
          <p className="user-email">{user.email}</p>

          {/* Username badge */}
          {user.username && (
            <div className="d-flex justify-content-center mb-3">
              <Badge className="username-badge">@{user.username}</Badge>
            </div>
          )}

          {/* Status */}
          <div className="d-flex justify-content-center mb-3">
            {getStatusBadge(user.status)}
          </div>

          {/* Role tags */}
          {user.roles && user.roles.length > 0 && (
            <div className="roles-container">
              {user.roles.slice(0, 3).map((role, index) => (
                <Badge
                  key={index}
                  className="role-tag"
                  style={{
                    backgroundColor: getRoleTagColor(role),
                    borderColor: getRoleTagColor(role),
                  }}
                >
                  {t(role) || role}
                </Badge>
              ))}
              {user.roles.length > 3 && (
                <Badge className="role-tag-more">
                  +{user.roles.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Created info */}
          {user.created_at && (
            <div className="last-login-info">
              <i className="bi bi-clock me-1"></i>
              <span>
                {t("last_login")}:{" "}
                {new Date(user.created_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Theme-Aware Styles */}
      <style>{`
        .user-card {
          border-radius: 20px !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          background: ${
            theme == "dark"
              ? "rgba(30, 41, 59, 0.8)"
              : "rgba(255, 255, 255, 0.95)"
          } !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid
            ${theme == "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          box-shadow: ${
            theme == "dark"
              ? "0 8px 32px rgba(0, 0, 0, 0.3)"
              : "0 8px 32px rgba(0, 0, 0, 0.08)"
          } !important;
          position: relative;
          overflow: hidden;
        }

        .user-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(
            90deg,
            #3b82f6,
            #10b981,
            #f59e0b,
            #ef4444,
            #8b5cf6
          );
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .user-card:hover {
          transform: translateY(-8px) !important;
          box-shadow: ${
            theme == "dark"
              ? "0 20px 40px rgba(0, 0, 0, 0.4)"
              : "0 20px 40px rgba(0, 0, 0, 0.15)"
          } !important;
        }

        .user-card:hover::before {
          opacity: 1;
        }

        .card-actions {
          position: absolute;
          top: 16px;
          right: 16px;
        }

        .action-btn {
          width: 36px !important;
          height: 36px !important;
          border-radius: 12px !important;
          border: none !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 14px !important;
          transition: all 0.3s ease !important;
          background: ${
            theme == "dark"
              ? "rgba(51, 65, 85, 0.8)"
              : "rgba(255, 255, 255, 0.95)"
          } !important;
          backdrop-filter: blur(10px) !important;
          box-shadow: ${
            theme == "dark"
              ? "0 4px 12px rgba(0, 0, 0, 0.3)"
              : "0 4px 12px rgba(0, 0, 0, 0.1)"
          } !important;
        }

        .edit-btn {
          color: ${theme == "dark" ? "#34d399" : "#10b981"} !important;
        }

        .edit-btn:hover {
          background: ${
            theme == "dark"
              ? "rgba(52, 211, 153, 0.2)"
              : "rgba(16, 185, 129, 0.1)"
          } !important;
          transform: scale(1.1) !important;
          box-shadow: ${
            theme == "dark"
              ? "0 6px 16px rgba(52, 211, 153, 0.3)"
              : "0 6px 16px rgba(16, 185, 129, 0.2)"
          } !important;
        }

        .delete-btn {
          color: ${theme == "dark" ? "#f87171" : "#ef4444"} !important;
        }

        .delete-btn:hover {
          background: ${
            theme == "dark"
              ? "rgba(248, 113, 113, 0.2)"
              : "rgba(239, 68, 68, 0.1)"
          } !important;
          transform: scale(1.1) !important;
          box-shadow: ${
            theme == "dark"
              ? "0 6px 16px rgba(248, 113, 113, 0.3)"
              : "0 6px 16px rgba(239, 68, 68, 0.2)"
          } !important;
        }

        .card-body-custom {
          padding: 2rem !important;
          text-align: center;
        }

        .avatar-container {
          position: relative;
          display: inline-block;
          margin-bottom: 1.5rem;
        }

        .user-avatar {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 1.5rem;
          margin: 0 auto;
          box-shadow: ${
            theme == "dark"
              ? "0 8px 24px rgba(0, 0, 0, 0.3)"
              : "0 8px 24px rgba(0, 0, 0, 0.15)"
          };
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .user-avatar::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .user-card:hover .user-avatar::before {
          opacity: 1;
        }

        .online-indicator {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 20px;
          height: 20px;
          background: #10b981;
          border-radius: 50%;
          border: 3px solid ${theme == "dark" ? "#1e293b" : "#ffffff"};
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
          }
          50% {
            box-shadow:
              0 2px 8px rgba(16, 185, 129, 0.4),
              0 0 0 8px rgba(16, 185, 129, 0.1);
          }
          100% {
            box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
          }
        }

        .user-name {
          color: ${theme == "dark" ? "#f1f5f9" : "#1e293b"} !important;
          font-weight: 600 !important;
          font-size: 1.1rem !important;
          margin-bottom: 0.5rem !important;
          line-height: 1.3;
        }

        .user-email {
          color: ${theme == "dark" ? "#94a3b8" : "#64748b"} !important;
          font-size: 0.9rem !important;
          margin-bottom: 1rem !important;
          word-break: break-word;
        }

        .username-badge {
          background: ${
            theme == "dark"
              ? "rgba(59, 130, 246, 0.2)"
              : "rgba(59, 130, 246, 0.1)"
          } !important;
          color: ${theme == "dark" ? "#93c5fd" : "#1d4ed8"} !important;
          border: 1px solid
            ${theme == "dark" ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"} !important;
          border-radius: 12px !important;
          padding: 0.375rem 0.75rem !important;
          font-size: 0.8rem !important;
          font-weight: 500 !important;
          font-family: "JetBrains Mono", "Fira Code", monospace;
        }

        .roles-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .role-tag {
          border-radius: 12px !important;
          padding: 0.375rem 0.75rem !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
          text-transform: capitalize !important;
          border: 1px solid transparent !important;
          transition: all 0.3s ease !important;
          position: relative;
          overflow: hidden;
        }

        .role-tag::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          transition: left 0.5s;
        }

        .role-tag:hover::before {
          left: 100%;
        }

        .role-tag-more {
          background: ${
            theme == "dark"
              ? "rgba(107, 114, 128, 0.2)"
              : "rgba(107, 114, 128, 0.1)"
          } !important;
          color: ${theme == "dark" ? "#d1d5db" : "#374151"} !important;
          border: 1px solid
            ${theme == "dark" ? "rgba(107, 114, 128, 0.3)" : "rgba(107, 114, 128, 0.2)"} !important;
          border-radius: 12px !important;
          padding: 0.375rem 0.75rem !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
        }

        .company-info {
          color: ${theme == "dark" ? "#cbd5e1" : "#475569"} !important;
          font-size: 0.85rem !important;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
        }

        .last-login-info {
          color: ${theme == "dark" ? "#94a3b8" : "#64748b"} !important;
          font-size: 0.8rem !important;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .card-body-custom {
            padding: 1.5rem !important;
          }

          .user-avatar {
            width: 64px;
            height: 64px;
            font-size: 1.25rem;
            border-radius: 16px;
          }

          .online-indicator {
            width: 16px;
            height: 16px;
            bottom: 2px;
            right: 2px;
          }

          .action-btn {
            width: 32px !important;
            height: 32px !important;
            font-size: 12px !important;
          }

          .card-actions {
            top: 12px;
            right: 12px;
          }

          .user-name {
            font-size: 1rem !important;
          }

          .user-email {
            font-size: 0.85rem !important;
          }
        }

        /* Animation for card entrance */
        .user-card {
          animation: cardEnter 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes cardEnter {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Loading shimmer effect */
        .user-card.loading {
          background: ${
            theme == "dark"
              ? "linear-gradient(90deg, rgba(30, 41, 59, 0.8) 25%, rgba(51, 65, 85, 0.8) 50%, rgba(30, 41, 59, 0.8) 75%)"
              : "linear-gradient(90deg, rgba(255, 255, 255, 0.95) 25%, rgba(248, 250, 252, 0.95) 50%, rgba(255, 255, 255, 0.95) 75%)"
          };
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        /* Focus states for accessibility */
        .action-btn:focus {
          outline: none !important;
          box-shadow: 0 0 0 3px
            ${theme == "dark" ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"} !important;
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .user-card {
            border: 2px solid ${theme == "dark" ? "#ffffff" : "#000000"} !important;
          }

          .action-btn {
            border: 1px solid ${theme == "dark" ? "#ffffff" : "#000000"} !important;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .user-card,
          .action-btn,
          .user-avatar,
          .online-indicator {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default UserCard;
